import type { PropsWithChildren } from "react";
import { createContext, memo, useContext, useEffect, useMemo, useState } from "react";

import { useConfig } from "@jti/app-context/useConfig";
import { isNonEmptyString } from "@jti/fe-common/utils";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import {
  useGetLastCustomerOrderQuery,
  useGetOrderAttributesQuery,
  useGetStoreConfigQuery,
} from "@jti/store/slices/magentoApiSlice";
import { useGetProfileQuery } from "@jti/store/slices/profileApiSlice";
import type { CheckoutGTMFlowType } from "@jti/tracker";
import { useTranslations } from "@jti/ui";

import { FISCAL_CODE_ATTRIBUTE_KEY } from "@checkout-v2/features/OrderAttributes/const";
import { selectVisibleAttributes } from "@checkout-v2/features/OrderAttributes/utils";
import { normalizeProfileExtraAttributes } from "@checkout-v2/features/OrderAttributes/utils/normalizeProfileExtraAttributes";
import {
  useAvailablePaymentMethodsForCart,
  usePreparedPaymentOptions,
} from "@checkout-v2/features/PaymentMethods/hooks";
import { addressHomeDeliveryFieldFactory } from "@checkout-v2/formFields";
import {
  useCartItems,
  useCartPrices,
  useCartSubscriptionStatus,
  useCheckoutV2Configuration,
  useDeliveryOptions,
  usePhoneValidation,
} from "@checkout-v2/shared";
import { useShippingFormDataContext } from "@checkout-v2/shared/providers/ShippingFormDataProvider";

import { useCheckoutFastFlow } from "../useCheckoutFastFlow";
import {
  buildPaymentStepFastFlowCandidate,
  FAST_FLOW_ORDER_ATTRIBUTE_PLACEMENTS,
  getCheckoutFlowTracking,
} from "../utils";

import type { CheckoutFastFlowContextValue } from "./CheckoutFastFlowContext.types";

const CheckoutFastFlowContext = createContext<CheckoutFastFlowContextValue | null>(null);

export const useCheckoutFastFlowContext = (): CheckoutFastFlowContextValue => {
  const context = useContext(CheckoutFastFlowContext);

  if (!context) {
    throw new Error("useCheckoutFastFlowContext must be used within CheckoutFastFlowProvider");
  }

  return context;
};

export const CheckoutFastFlowProvider = memo(({ children }: PropsWithChildren) => {
  const checkoutConfiguration = useCheckoutV2Configuration();
  const isFastFlowEnabled = checkoutConfiguration.fastFlowEnabled ?? false;

  if (!isFastFlowEnabled) {
    return <CheckoutFastFlowDisabledProvider>{children}</CheckoutFastFlowDisabledProvider>;
  }

  return <CheckoutFastFlowProviderInternal>{children}</CheckoutFastFlowProviderInternal>;
});

const CheckoutFastFlowDisabledProvider = memo(({ children }: PropsWithChildren) => {
  const checkoutFlowType = getCheckoutFlowTracking({
    shippingInfoPrefilled: false,
    paymentInfoPrefilled: false,
  }).checkoutFlowType;

  const value = useMemo<CheckoutFastFlowContextValue>(
    () => ({
      isCheckoutFastFlowInProgress: false,
      shippingInfoPrefilled: false,
      paymentInfoPrefilled: false,
      checkoutFlowType,
    }),
    [checkoutFlowType]
  );

  return <CheckoutFastFlowContext.Provider value={value}>{children}</CheckoutFastFlowContext.Provider>;
});

CheckoutFastFlowDisabledProvider.displayName = "CheckoutFastFlowDisabledProvider";

const CheckoutFastFlowProviderInternal = memo(({ children }: PropsWithChildren) => {
  const dispatch = useDispatch();

  const { initialDefaultValues, isShippingAgeVerificationEnabled } = useShippingFormDataContext();

  const storeConfigQueryState = useGetStoreConfigQuery();
  const { validatePhone } = usePhoneValidation();
  const profileQueryResult = useGetProfileQuery();
  const isAgeVerified = profileQueryResult.data?.is_age_verified === true;

  const { __ } = useTranslations();
  const config = useConfig();
  const checkoutConfiguration = useCheckoutV2Configuration();
  const { cartId, allDeliveryOptions, isLoading: isDeliveryOptionsLoading } = useDeliveryOptions();
  const { data: cartItems, isLoading: isCartItemsLoading, isFetching: isCartItemsFetching } = useCartItems();
  const { data: cartPrices } = useCartPrices();
  const { evoMethods, magentoMethods, isLoading: isPaymentMethodsLoading } = useAvailablePaymentMethodsForCart();
  const isSubscription = useCartSubscriptionStatus();

  const checkoutV2State = useSelector((state) => state.checkoutV2);

  const hasAnySavedSliceData =
    checkoutV2State.sectionsState.shippingSection === "completed" ||
    checkoutV2State.sectionsState.shippingSection === "editing";

  const lastOrderQueryResult = useGetLastCustomerOrderQuery(undefined, {
    skip: hasAnySavedSliceData,
  });
  const orderAttributesQueryResult = useGetOrderAttributesQuery(undefined, { skip: hasAnySavedSliceData });

  const checkoutFastFlowReady = !!(
    initialDefaultValues &&
    !!cartId &&
    profileQueryResult.status === "fulfilled" &&
    storeConfigQueryState.status === "fulfilled" &&
    lastOrderQueryResult.status === "fulfilled" &&
    orderAttributesQueryResult.status === "fulfilled" &&
    !isDeliveryOptionsLoading &&
    !isPaymentMethodsLoading &&
    !isCartItemsLoading &&
    !isCartItemsFetching &&
    !!cartPrices
  );

  /** Filter order attributes from form sections Fast Flow bypasses. */
  const normalizedProfileExtra = useMemo(
    () => normalizeProfileExtraAttributes(profileQueryResult.data?.extra || {}),
    [profileQueryResult.data?.extra]
  );

  const fastFlowVisibleOrderAttributes = useMemo(() => {
    if (!orderAttributesQueryResult.data) return [];

    return FAST_FLOW_ORDER_ATTRIBUTE_PLACEMENTS.flatMap((target) =>
      selectVisibleAttributes(orderAttributesQueryResult.data, normalizedProfileExtra, target)
    );
  }, [orderAttributesQueryResult.data, normalizedProfileExtra]);

  const hasDefaultAddress = addressHomeDeliveryFieldFactory.getDerivedConfig({
    countryName: config.COUNTRY_NAME,
    countryCode: config.ISO_COUNTRY_CODE,
    userProfileAddresses: profileQueryResult.data?.addresses ?? [],
  }).hasDefaultAddress;

  const lastOrderData = lastOrderQueryResult.data;

  const paymentOptions = usePreparedPaymentOptions({
    evoMethods,
    magentoMethods,
    selectedShippingMethodCode: "atHome", // hardcoded as fast-forward only works with atHome shipping method
    orderItems: cartItems,
  });

  const paymentStepFastFlowCandidate = useMemo(
    () => buildPaymentStepFastFlowCandidate({ lastOrderData, paymentOptions }),
    [lastOrderData, paymentOptions]
  );

  const [checkoutFlowType, setCheckoutFlowType] = useState<CheckoutGTMFlowType | null>(null);

  useEffect(() => {
    if (!checkoutFastFlowReady) {
      return;
    }

    setCheckoutFlowType(
      (currentFlowType) =>
        currentFlowType ??
        getCheckoutFlowTracking({
          shippingInfoPrefilled: !!hasDefaultAddress,
          paymentInfoPrefilled: !!paymentStepFastFlowCandidate,
        }).checkoutFlowType
    );
  }, [checkoutFastFlowReady, hasDefaultAddress, paymentStepFastFlowCandidate]);

  const { isCheckoutFastFlowInProgress } = useCheckoutFastFlow({
    ready: checkoutFastFlowReady,
    skip: hasAnySavedSliceData,
    initialDefaultValues,
    hasDefaultAddress,
    dispatch,
    cartId,
    validatePhone,
    checkoutConfiguration,
    allDeliveryOptions,
    isSubscription,
    lastOrderData,
    definedOrderAttributes: fastFlowVisibleOrderAttributes,
    paymentOptions,
    isBillingAddressVisibleForCheckout: config.isBillingAddressVisibleForCheckout,
    hasProfileFiscalCode: isNonEmptyString(profileQueryResult.data?.extra?.[FISCAL_CODE_ATTRIBUTE_KEY]),
    isShippingAgeVerificationEnabled,
    isAgeVerified,
    checkoutFlowType,
    shippingInfoPrefilled: !!hasDefaultAddress,
    paymentInfoPrefilled: !!paymentStepFastFlowCandidate,
  });

  const value = useMemo<CheckoutFastFlowContextValue>(
    () => ({
      isCheckoutFastFlowInProgress,
      shippingInfoPrefilled: !!hasDefaultAddress,
      paymentInfoPrefilled: !!paymentStepFastFlowCandidate,
      checkoutFlowType,
    }),
    [isCheckoutFastFlowInProgress, hasDefaultAddress, paymentStepFastFlowCandidate, checkoutFlowType]
  );

  return <CheckoutFastFlowContext.Provider value={value}>{children}</CheckoutFastFlowContext.Provider>;
});

CheckoutFastFlowProviderInternal.displayName = "CheckoutFastFlowProviderInternal";
